import { editProvider, getUpgradeType } from '@/services/flexGroup';
import { Form, Input, message, Modal, Space, Button, Select, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import type { ProviderType } from './index';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

interface EditProps {
  record: ProviderType | null;
  visible: boolean;
  refreshTable: () => void;
  onCancel: () => void;
}

interface Extra {
  tags: Array<{ key: string | undefined; value: string | undefined }>;
  upgradeTypes: string;
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
  },
};
const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 18, offset: 6 },
  },
};

const EditModal: React.FC<EditProps> = (editProps: EditProps) => {
  const { visible, onCancel, record, refreshTable } = editProps;
  const [form] = Form.useForm<ProviderType | Extra>();
  const [loading, setLoading] = useState(false);
  const [allUpgradeTypes, setAllUpgradeTypes] = useState<any>([]);

  const getUpgradeTypes = async () => {
    const res = await getUpgradeType();
    // console.log('getUpgradeTypes', res)
    if (res.code === 200) {
      setAllUpgradeTypes(
        res.data.details.map((item: any) => {
          return {
            value: item,
            label: item,
          };
        }),
      );
    }
  };

  useEffect(() => {
    if (record) {
      const _record = { ...record } as ProviderType | Extra;
      let tags: string | { [key: string]: string } = _record.tags as string;
      try {
        tags = JSON.parse(record.tags || '{}') as { [key: string]: string };
        _record.tags = Object.keys(tags).map((item) => ({
          key: item,
          value: (tags as { [key: string]: string })[item],
        }));
        _record.tags = _record.tags.length ? _record.tags : [{ key: undefined, value: undefined }];
      } catch (e) {}
      // console.log('_record', _record);
      form.setFieldsValue(_record);
      form.setFieldValue(
        'upgradeTypes',
        _record.upgradeTypes === '' ? [] : _record.upgradeTypes.split(','),
      );
    }
    getUpgradeTypes();
  }, []);

  const onOk = () => {
    form.validateFields().then((res) => {
      // console.log('onOk',res)
      setLoading(true);
      const tags: { [key: string]: string } = {};
      typeof res.tags !== 'string' &&
        res.tags.map((item) => {
          if (item.key) {
            tags[item.key] = item.value || '';
          }
          return item;
        });
      res.tags = JSON.stringify(tags);
      const id = editProps.record?.id;
      if (id) {
        editProvider({ ...res, id } as ProviderType)
          .then((msg) => {
            if (msg?.data.code === 'SUCCESS') {
              message.success('编辑成功');
              onCancel();
              refreshTable();
            }
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const upgradeTypesChange = (value: any) => {
    // console.log('editUpgradeTypes',value)
    if (value.length === 0) {
      form.setFieldValue('upgradeTypes', []);
    } else {
      form.setFieldValue('upgradeTypes', value);
    }
  };

  const onSwitch = (status: boolean) => {
    form.setFieldValue('publicIp', status);
  };

  return (
    <Modal
      title="编辑"
      open={visible}
      onCancel={onCancel}
      onOk={onOk}
      width={600}
      maskClosable={false}
      okButtonProps={{ loading: loading }}
    >
      <div className="ProiverEditModalContainer">
        <Form<ProviderType | Extra> labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} form={form}>
          <Form.Item label="名称" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="镜像id" name="imageId" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="供应商" name="type" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="收费类型" name="instanceChargeType" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="实例类型" name="instanceType" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="网络类型" name="networkType" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="网络计费类型" name="networkChargeType" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="区域" name="region" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="安全组" name="securityGroupIds" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="升级标识" name="upgradeTypes">
            <Select options={allUpgradeTypes} mode="tags" onChange={upgradeTypesChange} />
          </Form.Item>
          <Form.List name="tags">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Form.Item
                    {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                    label={index === 0 ? '实例tag信息' : ''}
                    required={false}
                    key={key}
                  >
                    <Space key={key} style={{ display: 'flex' }} align="baseline">
                      <Form.Item {...restField} name={[name, 'key']} noStyle>
                        <Input placeholder="key" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'value']} noStyle>
                        <Input placeholder="value" />
                      </Form.Item>
                      {fields.length > 1 ? (
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      ) : null}
                    </Space>
                  </Form.Item>
                ))}
                <Form.Item {...formItemLayoutWithOutLabel}>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    增加
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item label="用户数据" name="userData" rules={[{ required: true }]}>
            <Input.TextArea rows={5} />
          </Form.Item>
          <Form.Item label="zone配置信息" name="zoneConfig" rules={[{ required: true }]}>
            <Input.TextArea rows={5} />
          </Form.Item>
          <Form.Item label="是否配置公网ip" name="publicIp" rules={[{ required: true }]}>
            <Switch onChange={onSwitch} defaultChecked={record?.publicIp} />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default EditModal;
